import React, { useState, useEffect, useMemo } from "react"
import { Area, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from "recharts"
import { ChartContainer, ChartTooltip } from "../../../components/ui/line-charts-2"
import { computeLearningActivitySeries } from "../../../utils/dailyActivityHelpers"

const METRICS = [
  { key: "learners", label: "Learners" },
  { key: "sessions", label: "Sessions" },
  { key: "hours", label: "Hours" },
  { key: "lessons", label: "Lessons" },
  { key: "assessments", label: "Assessments" },
]

const PERIODS = [
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "90d", label: "90D" },
  { key: "12m", label: "12M" },
]

const CustomTooltip = ({ active, payload, metricLabel }) => {
  if (active && payload?.length) {
    return (
      <div className="rounded-lg bg-foreground text-background p-3 shadow-lg">
        <div className="text-xs font-medium mb-1 opacity-80">{metricLabel}</div>
        <div className="text-sm font-semibold">{payload?.[0]?.value?.toLocaleString()}</div>
      </div>
    )
  }
  return null
}

const chartConfig = { value: { label: "Activity", color: "var(--color-primary, #6366f1)" } }

const LearningActivityChart = ({ orgId }) => {
  const [metric, setMetric] = useState("learners")
  const [period, setPeriod] = useState("30d")
  const [series, setSeries] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    computeLearningActivitySeries(orgId, period).then((data) => {
      setSeries(data)
      setLoading(false)
    })
  }, [orgId, period])

  const activeMetric = METRICS?.find((m) => m?.key === metric)

  const { total, changeLabel } = useMemo(() => {
    const sum = series?.reduce((acc, d) => acc + (d?.[metric] || 0), 0) || 0
    const last = series?.[series?.length - 1]?.[metric] || 0
    const prev = series?.[series?.length - 2]?.[metric] || 0
    const pct = prev > 0 ? Math.round(((last - prev) / prev) * 1000) / 10 : null
    return { total: sum, changeLabel: pct }
  }, [series, metric])

  return (
    <div className="bg-card border border-border rounded-lg p-5 mb-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold text-foreground">Learning Activity</h3>

                <div className="hidden md:flex items-center gap-1">
          {PERIODS?.map((p) => (
            <button
              key={p?.key}
              onClick={() => setPeriod(p?.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                period === p?.key ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {p?.label}
            </button>
          ))}
        </div>

        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          aria-label="Select time period"
          className="md:hidden w-full text-sm font-medium rounded-md border border-border bg-card text-foreground px-3 py-2"
        >
          {PERIODS?.map((p) => (
            <option key={p?.key} value={p?.key}>
              {p?.label}
            </option>
          ))}
        </select>
      </div>

            <div className="hidden md:flex items-center gap-1 mb-4 flex-wrap">
        {METRICS?.map((m) => (
          <button
            key={m?.key}
            onClick={() => setMetric(m?.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              metric === m?.key
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border text-muted-foreground hover:bg-muted"
            }`}
          >
            {m?.label}
          </button>
        ))}
      </div>

      <select
        value={metric}
        onChange={(e) => setMetric(e.target.value)}
        aria-label="Select metric"
        className="md:hidden w-full mb-4 text-sm font-medium rounded-md border border-border bg-card text-foreground px-3 py-2"
      >
        {METRICS?.map((m) => (
          <option key={m?.key} value={m?.key}>
            {m?.label}
          </option>
        ))}
      </select>

      <div className="mb-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl font-bold text-foreground">{total?.toLocaleString()}</span>
          {changeLabel !== null && (
            <span className={`text-xs font-medium ${changeLabel >= 0 ? "text-success" : "text-destructive"}`}>
              {changeLabel >= 0 ? "+" : ""}
              {changeLabel}%
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1">{activeMetric?.label} over the selected period</p>
      </div>

      {loading ? (
        <div className="h-[280px] bg-muted rounded-lg animate-pulse" />
      ) : (
        <ChartContainer config={chartConfig} className="h-[280px] w-full">
          <ComposedChart data={series} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
            <defs>
              <linearGradient id="activityGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-primary, #6366f1)" stopOpacity={0.18} />
                <stop offset="100%" stopColor="var(--color-primary, #6366f1)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="4 12" stroke="var(--border)" horizontal vertical={false} />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} tickMargin={10} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12 }} tickMargin={10} allowDecimals={false} />
            <ChartTooltip content={<CustomTooltip metricLabel={activeMetric?.label} />} />
            <Area type="monotone" dataKey={metric} stroke="transparent" fill="url(#activityGradient)" dot={false} />
            <Line
              type="monotone"
              dataKey={metric}
              stroke="var(--color-primary, #6366f1)"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 5, fill: "var(--color-primary, #6366f1)", stroke: "white", strokeWidth: 2 }}
            />
          </ComposedChart>
        </ChartContainer>
      )}
    </div>
  )
}

export default LearningActivityChart